/**
 * `/api/license-holder` payloads — the license holder "tick" score, and the
 * key-guarded list of every holder.
 *
 * The score is a pure function of three inputs (daily rewards still held, lease
 * positions, staked balance) and is recomputed on every request at three
 * reference dates (now, -24h, -7d) so the UI can show a delta. Nothing is
 * persisted, which is why there is no table behind this group.
 */
import { z } from "zod";

import { isoInstantSchema, suiAddressSchema } from "../primitives";

/** Query for `GET /api/license-holder/score`. */
export const licenseHolderScoreQuerySchema = z.object({
  /**
   * Strict 64-hex form. This value is interpolated into a URL path against the
   * internal staking service, so a loose pattern would let `..%2F..%2Fadmin`
   * reach arbitrary paths on that host.
   */
  user_address: suiAddressSchema,
});

/** Resolved score query. */
export type LicenseHolderScoreQuery = z.infer<typeof licenseHolderScoreQuerySchema>;

/** One lease's contribution to the score, for the UI's per-lease breakdown. */
export const licenseHolderLeaseBreakdownSchema = z.object({
  id: z.number().int(),
  unclaimedTokens: z.number(),
  /** Base tier multiplier, or the post-maturity boosted value once matured. */
  multiplier: z.number(),
  /** Logarithmic term progress in `[0, 1]`. */
  progress: z.number(),
  score: z.number(),
  status: z.enum(["Matured", "Active"]),
  duration: z.number().int(),
  daysElapsed: z.number().int(),
  totalDays: z.number().int(),
});

/** Resolved per-lease breakdown. */
export type LicenseHolderLeaseBreakdown = z.infer<typeof licenseHolderLeaseBreakdownSchema>;

/** The raw inputs the score was computed from, echoed back for auditability. */
export const licenseHolderScoreRawSchema = z.object({
  activeLeases: z.number().int(),
  dailyRewardsBasis: z.number(),
  lockedTokens: z.number(),
  unlockedTokens: z.number(),
  leasingRewardsBasis: z.number(),
  stakedTokens: z.number(),
  stakedTokens45Day: z.number(),
  stakedTokens90Day: z.number(),
  totalClaimed: z.number(),
  neverClaimed: z.boolean(),
});

/** Resolved raw score inputs. */
export type LicenseHolderScoreRaw = z.infer<typeof licenseHolderScoreRawSchema>;

/** Score decomposition by source. */
export const licenseHolderScoreBreakdownSchema = z.object({
  dailyRewards: z.number(),
  leasingRewards: z.number(),
  staking: z.number(),
  /** The never-claimed bonus applied to the daily component (2.0 or 1.0). */
  multiplier: z.number(),
  individualLeases: z.array(licenseHolderLeaseBreakdownSchema),
  raw: licenseHolderScoreRawSchema,
});

/** Resolved score breakdown. */
export type LicenseHolderScoreBreakdown = z.infer<typeof licenseHolderScoreBreakdownSchema>;

/** One historical comparison point (`24h` or `7d`). */
export const licenseHolderScoreHistoryEntrySchema = z.object({
  score: z.number(),
  dailyRewardsBasis: z.number(),
  leasingRewardsBasis: z.number(),
  diffScore: z.number(),
  diffDailyRewards: z.number(),
  diffLeasingRewards: z.number(),
});

/** Resolved history entry. */
export type LicenseHolderScoreHistoryEntry = z.infer<typeof licenseHolderScoreHistoryEntrySchema>;

/** Response for `GET /api/license-holder/score`. */
export const licenseHolderScoreResponseSchema = z.object({
  /** Floored to an integer: ticks are whole units. */
  score: z.number().int(),
  breakdown: licenseHolderScoreBreakdownSchema,
  history: z.object({
    "24h": licenseHolderScoreHistoryEntrySchema,
    "7d": licenseHolderScoreHistoryEntrySchema,
  }),
  // `seasonInfo` (server clock + claim/earn checkpoint) was removed 2026-09-27
  // with the season checkpoints: there are no more seasons, and every consumer
  // of it was a checkpoint countdown or an earn-period claim block.
});

/** Resolved score response. */
export type LicenseHolderScoreResponse = z.infer<typeof licenseHolderScoreResponseSchema>;

/**
 * One wallet in `GET /api/license-holder/list`.
 *
 * "Holder" means `licenses.owner`, the address each license was minted TO: the
 * syncer writes it once and never follows a transfer (see apps/jobs/README.md). That is also who the
 * daily rewards pay, so the list agrees with the rewards — but it is not the
 * current on-chain owner of a license that has since moved.
 */
export const licenseHolderListEntrySchema = z.object({
  /** `licenses.owner` verbatim. Not re-validated: it is what the indexer stored. */
  address: z.string(),
  /** Every license with this owner, free and leased ones included — the same
   *  count `countLicensesByOwner` gives the rewards. */
  license_count: z.number().int().positive(),
  /** Earliest and latest mint event timestamps. Null when every row lacks one. */
  first_mint_at: isoInstantSchema.nullable(),
  last_mint_at: isoInstantSchema.nullable(),
});

/** Resolved holder-list entry. */
export type LicenseHolderListEntry = z.infer<typeof licenseHolderListEntrySchema>;

/** Response for `GET /api/license-holder/list`. */
export const licenseHolderListResponseSchema = z.object({
  /** Most licenses first, then by address, so the order is stable between calls. */
  holders: z.array(licenseHolderListEntrySchema),
  total_holders: z.number().int().nonnegative(),
  total_licenses: z.number().int().nonnegative(),
  generated_at: isoInstantSchema,
});

/** Resolved holder-list response. */
export type LicenseHolderListResponse = z.infer<typeof licenseHolderListResponseSchema>;
